// viewStorage — the boundary around the persisted View storage keys.
//
// Views were renamed Lens → Layout → View (CONTEXT.md §View System). Phase 3b
// (2026-08-13) finished the job by renaming the persisted keys too. This module
// is the ONE place in src/ that spells them, so a future rename is a one-file
// change again.
//
// Where the names live per backend:
//   · Supabase — the key IS the internal name, verbatim in items.custom_fields
//     (field maps are null on a Supabase-native space, so translation is
//     identity). Migration 20260813… re-keyed every existing row.
//   · Airtable — bases were NOT migrated: their columns keep the legacy titles
//     and each workspace's stored field map is still keyed by the old internal
//     name. fieldMap.js resolves that through LEGACY_INTERNAL_NAMES.
//
// Two record shapes flow through here:
//   · client nodes / itemRecords carry `_rawFields` (translated field names)
//   · server routes hold plain translated-fields objects — they use the
//     exported key constants directly (see api/workspace/[slug]/view*).

/** items.custom_fields key: JSON array of the item's View records. */
export const VIEWS_FIELD = 'Views';

/** Types key: JSON array of default Views inherited by Items of the Type. */
export const TYPE_DEFAULT_VIEWS_FIELD = 'Default Views';

/** Which View id opens first (item- and type-level). */
export const DEFAULT_VIEW_ID_FIELD = 'Default View ID';

// ─── Pre-rename keys, read-only ────────────────────────────────────────
// TEMPORARY. Every READ falls back to these so the window between "app deploys"
// and "deploy-migrations runs" doesn't show collections with no views — the
// migration can only run after the merge, so the two can't land atomically.
// Delete this block (and the `??` fallbacks below) once the migration has been
// applied to prod; nothing writes these keys any more.
export const LEGACY_VIEWS_FIELD = 'Lenses';
const LEGACY_TYPE_DEFAULT_VIEWS_FIELD = 'Default Lenses';
const LEGACY_DEFAULT_VIEW_ID_FIELD = 'Default Lens ID';

// ─── "Is this key plumbing?" ───────────────────────────────────────────
// Every key this module owns, both spellings, in one set.
const VIEW_STORAGE_KEYS = new Set([
  VIEWS_FIELD, TYPE_DEFAULT_VIEWS_FIELD, DEFAULT_VIEW_ID_FIELD,
  LEGACY_VIEWS_FIELD, LEGACY_TYPE_DEFAULT_VIEWS_FIELD, LEGACY_DEFAULT_VIEW_ID_FIELD,
]);

/**
 * True when a field key is View-config storage rather than somebody's content.
 * Surfaces that enumerate fields GENERICALLY — the Info tab's properties block,
 * the Edit tab's Field Layout, the Form editor — must skip these or they print
 * the raw views JSON on the page. Both spellings matter, and for two separate
 * reasons: an un-migrated Airtable space still carries the legacy internal name,
 * and a space that registered the old column as a *custom* field routes it
 * through the jsonb path where no internal-field blocklist can catch it.
 */
export function isViewStorageKey(key) {
  return VIEW_STORAGE_KEYS.has(key);
}

/**
 * The record's raw stored-views value — a JSON string on every writer's path,
 * but tolerate a real array too: `custom_fields` is jsonb, so a hand-edited or
 * script-written row can legitimately hold the array itself. Returns null when
 * the field is absent.
 */
export function rawStoredViews(record) {
  return record?._rawFields?.[VIEWS_FIELD] ?? record?._rawFields?.[LEGACY_VIEWS_FIELD] ?? null;
}

/** The raw stored-views JSON string, or null when absent / already an array. */
export function rawStoredViewsJson(record) {
  const raw = rawStoredViews(record);
  return typeof raw === 'string' ? raw : null;
}

/**
 * Parse a stored-views value → array. Accepts the JSON string every writer
 * produces AND a real jsonb array; [] on missing, malformed or non-array.
 */
export function parseStoredViews(value) {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** The node/record's stored Views ([] when absent or malformed). */
export function readStoredViews(record) {
  return parseStoredViews(rawStoredViews(record));
}

/** A display-type record's default Views ([] when absent or malformed). */
export function readTypeDefaultViews(typeRecord) {
  const raw = typeRecord?._rawFields?.[TYPE_DEFAULT_VIEWS_FIELD]
    ?? typeRecord?._rawFields?.[LEGACY_TYPE_DEFAULT_VIEWS_FIELD];
  return parseStoredViews(raw);
}

/**
 * True when the record stores at least one View. The string case (every
 * writer's shape) stays a string test rather than a parse — this runs in
 * list/nav hot paths — with the jsonb-array case handled by length.
 */
export function hasStoredViews(record) {
  const raw = rawStoredViews(record);
  if (Array.isArray(raw)) return raw.length > 0;
  if (typeof raw !== 'string') return false;
  const trimmed = raw.trim();
  return !!trimmed && trimmed !== '[]';
}

/**
 * The View id the item itself chose to open on (`Default View ID`), or null. Which items may use
 * it, and what it loses to, is utils/openingView.js's to say; this only reads the bytes.
 */
export function readDefaultViewId(record) {
  const raw = record?._rawFields?.[DEFAULT_VIEW_ID_FIELD] ?? record?._rawFields?.[LEGACY_DEFAULT_VIEW_ID_FIELD];
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

/** updateItemFields patch that sets the item's own opening View, or clears it with null. */
export function defaultViewIdPatch(viewId) {
  return { [DEFAULT_VIEW_ID_FIELD]: viewId || null };
}

/** True when the record links a Collection (either link-array or truthy scalar shape). */
export function hasCollectionLink(record) {
  const links = record?._rawFields?.['Collection'];
  return Array.isArray(links) ? links.length > 0 : !!links;
}

/**
 * The shared "view-shaped" test: an item that can open as a collection/view
 * page — it links a Collection or stores Views. Gates the View sidebar tab,
 * nav entries, featured-card navigation, and "Edit collection".
 */
export function isViewShaped(record) {
  return hasCollectionLink(record) || hasStoredViews(record);
}

/**
 * updateItemFields patch that persists a Views array (or pre-stringified
 * JSON). Spread into larger payloads: `{ Title, ...storedViewsPatch(views) }`.
 */
export function storedViewsPatch(viewsOrJson) {
  const json = typeof viewsOrJson === 'string' ? viewsOrJson : JSON.stringify(viewsOrJson);
  return { [VIEWS_FIELD]: json };
}

/**
 * Immutable `_rawFields` copy with the stored-views JSON replaced — the
 * optimistic-mirror idiom (state.nodes must reflect a write immediately so a
 * swap-views-and-back cycle re-resolves from fresh JSON, not the stale copy).
 */
export function withStoredViewsJson(rawFields, viewsOrJson) {
  return { ...(rawFields || {}), ...storedViewsPatch(viewsOrJson) };
}

// ─── Creation defaults ─────────────────────────────────────────────────
//
// What a View looks like the moment it is born, before anyone configures it.
// It lives here, beside the storage keys, because a creation default is only
// meaningful as bytes on the row: five call sites used to each spell
// `displayConfig: { legend: false }` from memory and the sixth — the fallback
// resolveViews synthesises for a collection that stores no views at all —
// never got the memo, so a collection made by "Turn into a collection" opened
// with a legend the same collection made by "+ Collection" did not have.
//
// What every new View starts with, decided by Vincent 2026-10-01 after changing the same three
// settings on every view he or an agent made:
//   · LEGEND OFF, on every type. It began as Gallery's alone — a left rail spending width on a key
//     nobody read — and the same is true of a table, a map or a browse. Hidden is not gone: the
//     gear's Show Legend toggle turns it back on per view.
//   · A MAP draws dots, not clusters (`noCluster`), with each item's label on HOVER rather than
//     always (`itemLabelMode`), which is what keeps a dense map readable.
//   · A TABLE puts its Fields control (which columns show) in the toolbar rather than inside the
//     settings gear, because choosing columns is the thing a table is for.
// Added 2026-11-04, from Vincent's review of a freshly imported space (Transformational Communities),
// as the starting template for every new view:
//   · SEARCH is the small magnifier, not a box (`searchStyle: 'icon'`) — the box took the width the
//     filter chips need.
//   · SORT is off the toolbar on a gallery, list or table: most collections have no order a reader
//     wants to change, and the gear still sets it.
//   · A TABLE opens on its filled columns only (`list.hiddenFields: { auto: true }`, read by
//     GridCanvas/columnDefaults.js effectiveHiddenIds): an empty column hides, and so does Item
//     Display Type when every row is one type.
// Two more rules from the same review are LIVE rather than seeded, because they follow what the view
// is doing (registry.js `zoneFor`): Group reaches the toolbar only once the view is grouped, and
// "+ Filter" steps aside once filter chips are pinned.
// A default only seeds a new view; it never rewrites a stored one.
const SORT_OFF_TOOLBAR = new Set(['gallery', 'list', 'table']);

/** The displayConfig a newly created View of `type` starts with. */
export function freshViewDisplayConfig(type) {
  const base = { legend: false, searchStyle: 'icon' };
  const controls = SORT_OFF_TOOLBAR.has(type) ? { sort: { zone: 'settings' } } : null;
  if (type === 'map') return { ...base, noCluster: true, itemLabelMode: 'hover' };
  if (type === 'table') {
    return { ...base, controls: { ...controls, hideFields: { zone: 'toolbar' } }, list: { hiddenFields: { auto: true } } };
  }
  return controls ? { ...base, controls } : base;
}

/**
 * The Gallery View a collection is born with — the stored default every create
 * path writes, and the shape resolveViews falls back to when a collection has
 * no stored views to show.
 */
export function freshGalleryView(id = 'default') {
  return { id, name: 'Gallery', type: 'gallery', displayConfig: freshViewDisplayConfig('gallery') };
}

/** Reserved id (and type) of the synthesised SELF view — the item as a page. */
export const PAGE_VIEW_ID = 'page';

/**
 * Reserved id (and type) of the other synthesised self view — the item as a simple form for the
 * people who can edit it (src/views/profile). Unlike Page it stores NOTHING, not even a marker:
 * whether its tab shows is a per-type setting in the space config, not a per-item one.
 */
export const PROFILE_VIEW_ID = 'profile';

/**
 * Reserved id (and type) of the third synthesised self view — the item's recording or transcript
 * with its chapters and notes (src/views/watch). Like Profile it stores nothing on an item: it is
 * offered wherever there is something to play or a transcript. A type's Default Views may still
 * carry a `watch` entry, read by resolveViews as a MARKER placing it, the way a Page entry is.
 */
export const WATCH_VIEW_ID = 'watch';

/**
 * Reserved id (and type) of the fourth synthesised self view — the item's PDF with its notes, summary
 * and page (src/views/read). Like Watch it stores nothing on an item: it is offered wherever the
 * item's content is a PDF, and a `read` entry in a type's Default Views is read as a MARKER placing it.
 */
export const READ_VIEW_ID = 'read';

/**
 * The array to persist for a resolved view list.
 *
 * Page is synthesised by resolveViews on every resolve, so what's stored for it
 * is a bare MARKER: its presence means "show this item's Page pill, here", and
 * a Page the author removed stores nothing at all. Storing the resolved view
 * itself would put a renamable, duplicable row in the JSON for something that
 * is regenerated anyway.
 *
 * BOTH writers go through this — ViewEditor's explicit save and the debounced
 * useItemViewsAutoSave. They previously disagreed (one filtered `page`, one
 * didn't), which made the stored bytes depend on whichever wrote last; now the
 * rule has one home. Critically, the autosave must NOT strip a marker the user
 * just added, or the pill would vanish on the next position tick.
 */
export function toStoredViews(views) {
  const out = [];
  for (const v of views || []) {
    // Synthesised on every resolve and configured per type, so there is nothing of it to keep.
    if (v?.id === PROFILE_VIEW_ID || v?.type === PROFILE_VIEW_ID) continue;
    if (v?.id === WATCH_VIEW_ID || v?.type === WATCH_VIEW_ID) continue;
    if (v?.id === READ_VIEW_ID || v?.type === READ_VIEW_ID) continue;
    if (v?.id === PAGE_VIEW_ID || v?.type === PAGE_VIEW_ID) {
      if (!v.hidden) out.push({ id: PAGE_VIEW_ID, type: PAGE_VIEW_ID, name: v.name || 'Page' });
      continue;
    }
    out.push(v);
  }
  return out;
}

/**
 * Is this item a dashboard — an Item carrying a View of type `dashboard`?
 *
 * Lives here because it is a question about STORED VIEWS, and it lives in ONE place because
 * the desktop sidebar and the mobile More menu both have to answer it the same way. They
 * already drifted once over Favorites, when only one of them canonicalised a renamed action
 * id, and the symptom was a section that listed different things on the two platforms.
 */
export function isDashboardItem(record) {
  if (!isViewShaped(record)) return false;
  try { return readStoredViews(record).some((v) => v?.type === 'dashboard'); } catch { return false; }
}
